import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SiteConfigService, SiteNavItem, SiteSolution } from '../core/site-config.service';
import { ThemeModeService } from '../core/theme-mode.service';
import { AuthService } from '../core/auth.service';
import { AuthWidgetComponent } from '../widgets/auth/auth-widget.component';
import { capabilityForPath } from '../core/site-route.guard';
import { FortuneCategory, FortuneService } from '../widgets/fortune/fortune.service';
import { CommerceCartService } from '../core/commerce-cart.service';
import { localizedSolution } from '../widgets/aymudo/aymudo-solutions.content';
import { ApiClientService } from '../core/api-client.service';
import { firstValueFrom } from 'rxjs';

type AymudoMegaGroup = {
  title: string;
  links: Array<{ label: string; description: string; path: string; fragment?: string }>;
};

@Component({
  selector: 'app-site-header',
  imports: [AuthWidgetComponent, NgTemplateOutlet, RouterLink, RouterLinkActive],
  template: `
    @if (shell().enabled) {
      @if (showTopMenuCta()) {
        <div class="header-top-menu">
          @if (showSellerPresence()) {
            <div class="seller-presence" aria-label="Falcı durumu">
              <button type="button" [class.active]="sellerPresence()==='online'" [attr.aria-pressed]="sellerPresence()==='online'" [disabled]="sellerPresenceSaving()" (click)="setSellerPresence('online')">{{presenceCopy().online}}</button>
              <button type="button" [class.active]="sellerPresence()==='on_duty'" [attr.aria-pressed]="sellerPresence()==='on_duty'" [disabled]="sellerPresenceSaving()" (click)="setSellerPresence('on_duty')">{{presenceCopy().duty}}</button>
              <button type="button" [class.active]="sellerPresence()==='offline'" [attr.aria-pressed]="sellerPresence()==='offline'" [disabled]="sellerPresenceSaving()" (click)="setSellerPresence('offline')">{{presenceCopy().offline}}</button>
              <span class="seller-presence-feedback" [class.error]="sellerPresenceError()" aria-live="polite">{{ sellerPresenceFeedback() }}</span>
            </div>
          }
          @if (showCreditBalance()) {
            <a class="credit-header-link" [routerLink]="i18n.routePath('creditTopUp')">
              {{ creditBalanceText() }} {{ creditLabel() }}
            </a>
          }
          <a [routerLink]="sellerCtaLink()" class="become-reader-cta">
            <span>✦</span>
            <strong>{{ sellerCtaLabel() }}</strong>
          </a>
        </div>
      }

      @if (site.config().brandKey === 'aymudo') {
        <div class="ay-customer-service-career-bar">
          <a routerLink="/customer-service">{{ customerServiceCareerLabel() }} <span>↗</span></a>
        </div>
      }

      <header
        class="site-header"
        [class.hidden]="headerHidden()"
        [attr.data-theme-variant]="site.config().themeEngine.header"
      >
        <a
          class="brand"
          [routerLink]="i18n.routePath('home')"
          [attr.aria-label]="site.config().name"
        >
          @if (
            site.config().branding.logoLightUrl ||
            site.config().branding.logoDarkUrl ||
            site.config().branding.logoUrl
          ) {
            <img
              class="brand-logo theme-logo-light"
              [src]="
                site.config().branding.logoLightUrl ||
                site.config().branding.logoUrl ||
                site.config().branding.logoDarkUrl
              "
              [alt]="site.config().name"
            />
            <img
              class="brand-logo theme-logo-dark"
              [src]="
                site.config().branding.logoDarkUrl ||
                site.config().branding.logoUrl ||
                site.config().branding.logoLightUrl
              "
              [alt]="site.config().name"
            />
          } @else {
            <span class="brand-mark">{{ brandMark() }}</span>
          }
          @if (
            shell().showBrand &&
            !(
              site.config().branding.logoLightUrl ||
              site.config().branding.logoDarkUrl ||
              site.config().branding.logoUrl
            )
          ) {
            <span class="brand-copy">
              <strong>{{ site.config().name }}</strong>
              <small>{{ tagline() }}</small>
            </span>
          }
        </a>

        @if (shell().showNav) {
          <nav
            class="nav desktop-nav"
            data-theme-shell-edit="header"
            [attr.aria-label]="i18n.t('mainNavigation')"
          >
            @for (item of navItems(); track item.id || item.routeKey || item.path) {
              @if (isAymudoMega(item)) {
                <span class="nav-dropdown ay-mega-nav" data-menu-mode="mega">
                  <a [routerLink]="navLink(item)" routerLinkActive="active"
                    >{{ navLabel(item) }} <i>⌄</i></a
                  >
                  <span class="ay-mega-panel">
                    <span class="ay-mega-intro">
                      <small>{{ aymudoMegaIntro(item.routeKey).eyebrow }}</small>
                      <strong>{{ aymudoMegaIntro(item.routeKey).title }}</strong>
                      <a [routerLink]="navLink(item)"
                        >{{ aymudoMegaIntro(item.routeKey).all }} <b>→</b></a
                      >
                    </span>
                    <span class="ay-mega-groups">
                      @for (group of aymudoMegaGroups(item.routeKey); track group.title) {
                        <span class="ay-mega-group"
                          ><small>{{ group.title }}</small>
                          @for (link of group.links; track link.label) {
                            <a [routerLink]="link.path" [fragment]="link.fragment"
                              ><span
                                ><strong>{{ link.label }}</strong
                                ><em>{{ link.description }}</em></span
                              ><b>↗</b></a
                            >
                          }
                        </span>
                      }
                    </span>
                    <a class="ay-mega-subscribe" [routerLink]="i18n.routePath('subscribe')"
                      ><small>{{ subscribeCopy().eyebrow }}</small
                      ><strong>{{ subscribeCopy().title }}</strong
                      ><b>↗</b></a
                    >
                  </span>
                </span>
              } @else if (
                (item.menuMode === 'dropdown' || item.menuMode === 'mega' || !item.menuMode) &&
                item.children?.length
              ) {
                <span class="nav-dropdown" [attr.data-menu-mode]="item.menuMode || 'dropdown'">
                  <a [routerLink]="navLink(item)" routerLinkActive="active"
                    >{{ navLabel(item) }} <i>⌄</i></a
                  >
                  <span class="nav-dropdown-panel">
                    <small>{{ item.description || navLabel(item) }}</small>
                    @for (child of item.children; track child.id || child.path) {
                      @if (child.enabled !== false) {
                        <a [routerLink]="navLink(child)"
                          ><span>{{ navLabel(child) }}</span
                          ><b>↗</b></a
                        >
                      }
                    }
                  </span>
                </span>
              } @else if (isAymudoSolutions(item)) {
                <a
                  [routerLink]="navLink(item)"
                  fragment="cozumler"
                  routerLinkActive="active"
                  data-analytics-action="nav.solutions"
                  >{{ navLabel(item) }}</a
                >
              } @else {
                <a
                  [routerLink]="navLink(item)"
                  [fragment]="item.fragment"
                  routerLinkActive="active"
                  [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
                  [attr.data-analytics-action]="'nav.' + (item.routeKey || item.path)"
                >
                  {{ navLabel(item) }}
                </a>
              }
            }
          </nav>
        }

        <div class="header-actions">
          @if (showHeaderLanguage()) {
            <div class="header-language" [attr.aria-label]="i18n.t('language')">
              <span aria-hidden="true">◎</span>
              <select
                [attr.aria-label]="i18n.t('language')"
                [value]="i18n.locale()"
                (change)="setHeaderLocale($event)"
              >
                @for (locale of i18n.supportedLocales(); track locale.code) {
                  <option [value]="locale.code">{{ locale.label }}</option>
                }
              </select>
            </div>
          }
          @if (shell().showThemeSwitcher) {
            <ng-container *ngTemplateOutlet="themeSwitch" />
          }

          @if (site.config().brandKey === 'aymudo') {
            <a class="ay-subscribe-cta" [routerLink]="i18n.routePath('subscribe')">{{
              subscribeCopy().button
            }}</a>
          }

          @if (shell().showAuth) {
            <app-auth-widget data-theme-shell-edit="user" />
          }

          @if (cartEnabled()) {
            <button
              type="button"
              class="cart-button"
              data-cart-target
              (click)="cartOpen.update((open) => !open)"
              [attr.aria-label]="i18n.t('cart')"
            >
              <span>⌑</span><strong>{{ i18n.t('cart') }}</strong
              ><b>{{ cart.count() }}</b>
            </button>
          }

          <button
            type="button"
            class="menu-button"
            [class.open]="menuOpen()"
            (click)="toggleMenu()"
            [attr.aria-label]="menuOpen() ? i18n.t('closeMenu') : i18n.t('openMenu')"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      @if (commerce() && headerCategories().length) {
        <nav class="commerce-category-bar" [attr.aria-label]="i18n.t('categories')">
          <a class="all-categories" [routerLink]="i18n.routePath('fortune')">{{
            i18n.t('allCategories')
          }}</a>
          @for (category of headerCategories(); track category.id) {
            @if (!category.parentID) {
              <span class="category-menu-item">
                <a [routerLink]="i18n.routePath('category', { slug: category.slug })">{{
                  category.title
                }}</a>
                @if (categoryChildren(category.id).length) {
                  <span class="category-mega">
                    <strong>{{ category.title }}</strong>
                    @for (child of categoryChildren(category.id); track child.id) {
                      <a [routerLink]="i18n.routePath('category', { slug: child.slug })">{{
                        child.title
                      }}</a>
                    }
                  </span>
                }
              </span>
            }
          }
        </nav>
      }

      @if (cartEnabled() && cartOpen()) {
        <aside class="mini-cart">
          <header>
            <strong>Sepetin</strong><button type="button" (click)="cartOpen.set(false)">×</button>
          </header>
          @for (item of cart.items(); track item.listingID) {
            <div class="mini-cart-item">
              @if (item.imageUrl) {
                <img [src]="item.imageUrl" [alt]="item.title" />
              }
              <span
                ><strong>{{ item.title }}</strong
                ><small>{{ item.quantity }} adet</small></span
              >
            </div>
          } @empty {
            <p>Sepetin henüz boş.</p>
          }
          <a
            class="mini-cart-link"
            [routerLink]="i18n.routePath('cart')"
            (click)="cartOpen.set(false)"
            >Sepete git</a
          >
        </aside>
      }

      <aside
        class="menu-sheet"
        [class.open]="menuOpen()"
        (click)="closeMenu()"
        [attr.aria-label]="i18n.t('mobileNavigation')"
        [attr.aria-hidden]="menuOpen() ? null : 'true'"
        [attr.inert]="menuOpen() ? null : ''"
        role="dialog"
        aria-modal="true"
      >
        <div class="sheet-card" (click)="$event.stopPropagation()">
          <div class="sheet-head">
            @if (
              site.config().branding.logoLightUrl ||
              site.config().branding.logoDarkUrl ||
              site.config().branding.logoUrl
            ) {
              <img
                class="brand-logo small theme-logo-light"
                [src]="
                  site.config().branding.logoLightUrl ||
                  site.config().branding.logoUrl ||
                  site.config().branding.logoDarkUrl
                "
                [alt]="site.config().name"
              />
              <img
                class="brand-logo small theme-logo-dark"
                [src]="
                  site.config().branding.logoDarkUrl ||
                  site.config().branding.logoUrl ||
                  site.config().branding.logoLightUrl
                "
                [alt]="site.config().name"
              />
            } @else {
              <span class="brand-mark small">{{ brandMark() }}</span>
            }
            <div>
              <strong>{{ site.config().name }}</strong>
              <small>{{ tagline() }}</small>
            </div>
            <button
              type="button"
              class="sheet-close"
              (click)="closeMenu()"
              [attr.aria-label]="i18n.t('closeMenu')"
            >
              ×
            </button>
          </div>

          @if (shell().showNav) {
            <nav class="sheet-nav" [attr.aria-label]="i18n.t('menu')">
              @for (item of navItems(); track item.id || item.routeKey || item.path) {
                <a
                  [routerLink]="navLink(item)"
                  [fragment]="item.fragment"
                  routerLinkActive="active"
                  (click)="closeMenu()"
                >
                  {{ navLabel(item) }}
                </a>
                @if (isAymudoMega(item)) {
                  <div class="ay-mobile-mega">
                    @for (group of aymudoMegaGroups(item.routeKey); track group.title) {
                      <strong>{{ group.title }}</strong>
                      @for (link of group.links; track link.label) {
                        <a [routerLink]="link.path" [fragment]="link.fragment" (click)="closeMenu()"
                          >{{ link.label }} <span>↗</span></a
                        >
                      }
                    }
                  </div>
                }
                @if (item.children?.length) {
                  <div class="sheet-solution-links">
                    @for (child of item.children; track child.id || child.path) {
                      @if (child.enabled !== false) {
                        <a [routerLink]="navLink(child)" (click)="closeMenu()"
                          >{{ navLabel(child) }} <span>↗</span></a
                        >
                      }
                    }
                  </div>
                }
              }
              @if (site.config().brandKey === 'aymudo') {
                <a
                  class="ay-mobile-customer-service-career"
                  routerLink="/customer-service"
                  (click)="closeMenu()"
                  >{{ customerServiceCareerLabel() }} <span>↗</span></a
                >
                <a
                  class="ay-mobile-subscribe"
                  [routerLink]="i18n.routePath('subscribe')"
                  (click)="closeMenu()"
                  >{{ subscribeCopy().button }} <span>↗</span></a
                >
              }
              @if (site.enabled('liveSupport')) {
                <button class="mobile-live-support-link" type="button" (click)="openLiveSupport()">
                  ✦ Canlı destek
                </button>
              }
              @if (commerce()) {
                <strong class="sheet-nav-title">{{ i18n.t('categories') }}</strong>
                <a [routerLink]="i18n.routePath('fortune')" (click)="closeMenu()">{{
                  i18n.t('allCategories')
                }}</a>
                @for (category of headerCategories(); track category.id) {
                  <a
                    [routerLink]="i18n.routePath('category', { slug: category.slug })"
                    (click)="closeMenu()"
                    >{{ category.title }}</a
                  >
                }
              }
            </nav>
          }

          @if (site.enabled('guidedActions')) {
            <div class="sheet-mobile-actions">
              <button
                class="sheet-guided-action"
                type="button"
                (click)="openGuidedAction()"
              >
                <span>✦</span>
                <strong>{{ guidedActionLabel() }}</strong>
                <b>↗</b>
              </button>
            </div>
          }

          @if (shell().showThemeSwitcher) {
            <div class="sheet-theme">
              <span>{{ i18n.t('themeMode') }}</span>
              <ng-container *ngTemplateOutlet="themeSwitch" />
            </div>
          }

          @if (shell().showAuth) {
            <div class="sheet-auth">
              <app-auth-widget />
            </div>
          }
        </div>
      </aside>

      <nav
        class="mobile-dock"
        [class.service-dock]="isServiceSite()"
        [attr.aria-label]="i18n.t('mobileNavigation')"
      >
        @for (item of dockItems(); track item.key) {
          <a
            [routerLink]="item.path"
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: item.exact }"
            (click)="item.key === 'menu' ? toggleMenu() : closeMenu()"
          >
            <span>{{ item.icon }}</span>
            <small>{{ item.label }}</small>
          </a>
        }
        <button type="button" [class.active]="menuOpen()" (click)="toggleMenu()" [attr.aria-label]="menuOpen() ? i18n.t('closeMenu') : i18n.t('openMenu')">
          <span>☰</span>
          <small>{{ i18n.t('menu') }}</small>
        </button>
      </nav>

      <ng-template #themeSwitch>
        <div
          class="theme-orbit"
          [class.light]="themeMode.selectedMode() === 'light'"
          [class.auto]="themeMode.selectedMode() === 'auto'"
          [class.dark]="themeMode.selectedMode() === 'dark'"
          [attr.aria-label]="i18n.t('themeSelection')"
        >
          <button
            type="button"
            class="sun"
            [class.active]="themeMode.selectedMode() === 'light'"
            (click)="themeMode.setMode('light')"
            [attr.aria-label]="i18n.t('lightTheme')"
          >
            <span>☀</span>
          </button>
          <button
            type="button"
            class="auto"
            [class.active]="themeMode.selectedMode() === 'auto'"
            (click)="themeMode.setMode('auto')"
            [attr.aria-label]="i18n.t('autoTheme')"
          >
            <span>◐</span>
          </button>
          <button
            type="button"
            class="moon"
            [class.active]="themeMode.selectedMode() === 'dark'"
            (click)="themeMode.setMode('dark')"
            [attr.aria-label]="i18n.t('darkTheme')"
          >
            <span>☾</span>
          </button>
          <i class="orbit-thumb"></i>
          <i class="spark s1"></i>
          <i class="spark s2"></i>
          <i class="spark s3"></i>
        </div>
      </ng-template>
    }
  `,
  styleUrl: './site-header.component.scss',
})
export class SiteHeaderComponent implements AfterViewInit, OnDestroy {
  protected readonly site = inject(SiteConfigService);
  protected readonly i18n = inject(I18nService);
  protected readonly themeMode = inject(ThemeModeService);
  protected readonly auth = inject(AuthService);
  private readonly fortune = inject(FortuneService);
  private readonly api = inject(ApiClientService);
  protected readonly cart = inject(CommerceCartService);
  protected readonly shell = computed(() => this.site.config().shell.header);
  protected readonly navItems = computed(() => {
    let items = this.shell().nav.filter(
      (item) => item.enabled && !this.shouldHideNavItem(item) && this.pathEnabled(item.path),
    );
    if (
      this.site.experience('fortune') &&
      this.auth.loggedIn() &&
      !items.some((item) => item.routeKey === 'orders')
    ) {
      const readings: SiteNavItem = {
        label: 'Fallarım',
        path: '/fallarim',
        routeKey: 'orders',
        enabled: true,
      };
      const readersIndex = items.findIndex((item) => item.routeKey === 'sellers');
      items = readersIndex >= 0
        ? [...items.slice(0, readersIndex + 1), readings, ...items.slice(readersIndex + 1)]
        : [readings, ...items];
    }
    if (this.site.config().brandKey === 'aymudo' && this.i18n.contentLang() === 'tr' && !items.some(item=>item.routeKey==='managedServices')) {
      const services={label:'Hizmetler',path:'/hizmetler',routeKey:'managedServices',enabled:true,menuMode:'dropdown',children:[
        {label:'Uzaktan sekreterlik',path:'/hizmetler/uzaktan-sekreterlik',enabled:true},
        {label:'Reklam yönetimi',path:'/hizmetler/reklam-yonetimi',enabled:true},
        {label:'Sosyal medya yönetimi',path:'/hizmetler/sosyal-medya-yonetimi',enabled:true},
        {label:'Mesaj cevaplama',path:'/hizmetler/mesaj-cevaplama',enabled:true}
      ]} as SiteNavItem;
      const productsIndex=items.findIndex(item=>item.routeKey==='products');
      items=productsIndex>=0?[...items.slice(0,productsIndex+1),services,...items.slice(productsIndex+1)]:[...items,services];
    }
    if (
      this.site.experience('fortune') &&
      !items.some((item) => item.path === '/forum')
    ) {
      return [...items, { label: 'Forum', path: '/forum', enabled: true } as SiteNavItem];
    }
    return items;
  });
  protected readonly brandMark = computed(() => this.site.config().branding.mark);
  protected readonly tagline = computed(() =>
    this.site.experience('fortune')
      ? this.i18n.t('brandTagline')
      : this.site.text('brandTagline', this.site.config().branding.tagline),
  );
  protected readonly showHeaderLanguage = computed(
    () =>
      ['ceondent', 'aymudo'].includes(this.site.config().brandKey) &&
      this.i18n.supportedLocales().length > 1,
  );
  protected setHeaderLocale(event: Event): void {
    this.i18n.setLang((event.target as HTMLSelectElement).value);
  }
  protected readonly menuOpen = signal(false);
  protected readonly cartOpen = signal(false);
  protected readonly headerCategories = signal<FortuneCategory[]>([]);
  private bodyOverflowBeforeMenu = '';
  // Service packages must never fall back to the marketplace dock, even if a
  // stale capability payload temporarily says commerce is enabled.
  protected readonly isServiceSite = computed(() => {
    const config = this.site.config();
    return (
      ['serviceMarketplace', 'appointmentMarketplace', 'veterinaryMarketplace'].includes(config.businessMode || '') ||
      ['serviceBusiness', 'serviceSocialBusiness'].includes(config.packageKey) ||
      (this.site.enabled('appointments') && !this.site.enabled('commerce'))
    );
  });
  protected readonly commerce = computed(
    () =>
      this.site.enabled('commerce') &&
      !this.isServiceSite() &&
      !this.site.experience('fortune') && this.site.config().brandKey !== 'aymudo',
  );
  protected readonly cartEnabled = computed(
    () => this.commerce() && this.site.config().capabilities.cart !== false,
  );
  protected readonly headerHidden = signal(false);
  protected readonly sellerPresence = signal<'online'|'on_duty'|'offline'>('online');
  protected readonly sellerPresenceSaving = signal(false);
  protected readonly sellerPresenceFeedback = signal('');
  protected readonly sellerPresenceError = signal(false);
  protected readonly presenceCopy=computed(()=>({
    tr:{online:'Çevrimiçi',duty:'Nöbetçi',offline:'Çevrimdışı',saving:'Kaydediliyor…',failed:'Durum kaydedilemedi. Tekrar deneyin.',saved:'Durum güncellendi'},
    en:{online:'Online',duty:'On duty',offline:'Offline',saving:'Saving…',failed:'Status could not be saved. Please try again.',saved:'Status updated'},
    de:{online:'Online',duty:'Im Dienst',offline:'Offline',saving:'Wird gespeichert…',failed:'Status konnte nicht gespeichert werden. Bitte erneut versuchen.',saved:'Status aktualisiert'},
    es:{online:'En línea',duty:'De guardia',offline:'Desconectado',saving:'Guardando…',failed:'No se pudo guardar el estado. Inténtalo de nuevo.',saved:'Estado actualizado'},
    fr:{online:'En ligne',duty:'De garde',offline:'Hors ligne',saving:'Enregistrement…',failed:'Impossible d’enregistrer le statut. Réessayez.',saved:'Statut mis à jour'}
  }[this.i18n.lang()]));
  protected creditLabel():string{return({tr:'Kredi',en:'Credits',de:'Guthaben',es:'Créditos',fr:'Crédits'} as Record<string,string>)[this.i18n.lang()]??'Credits'}
  protected creditBalanceText():string{return new Intl.NumberFormat(this.i18n.locale(),{minimumFractionDigits:0,maximumFractionDigits:2}).format(Number(this.auth.creditBalance()??0))}
  protected readonly showSellerPresence = computed(() => this.site.experience('fortune') && this.currentUserIsSeller());
  private presenceTimer: ReturnType<typeof setInterval> | null = null;
  private presenceStarted = false;
  protected openLiveSupport() {
    this.closeMenu();
    if (typeof window !== 'undefined')
      window.setTimeout(() => window.dispatchEvent(new CustomEvent('aymudo-open-live-support')), 0);
  }
  protected openGuidedAction(): void {
    this.closeMenu();
    if (typeof window !== 'undefined') {
      window.setTimeout(() => window.dispatchEvent(new CustomEvent('aymudo-open-guided-action')), 0);
    }
  }
  protected readonly showTopMenuCta = computed(() => {
    if (this.site.config().businessMode === 'qrMarketplace') return this.auth.loggedIn();
    if (this.site.config().brandKey === 'aymudo' || this.isServiceSite()) return false;
    const seller = this.currentUserIsSeller();
    return seller
      ? this.site.enabled('sellerPanel') && !!this.site.config().routes['sellerDashboard']
      : this.site.enabled('sellerOnboarding') && !!this.site.config().routes['becomeSeller'];
  });
  protected readonly showCreditBalance = computed(
    () =>
      this.site.experience('fortune') &&
      !!this.site.config().routes['creditTopUp'] &&
      this.auth.loggedIn(),
  );
  protected readonly sellerCtaLink = computed(() =>
    this.site.config().businessMode === 'qrMarketplace'
      ? '/yonetim'
      : this.currentUserIsSeller()
      ? this.i18n.routePath('sellerDashboard')
      : this.i18n.routePath('becomeSeller'),
  );
  protected readonly sellerCtaLabel = computed(() =>
    this.site.config().businessMode === 'qrMarketplace'
      ? 'Yönetim Paneli'
      : this.currentUserIsSeller()
      ? this.site.text('sellerPanel', this.i18n.t('sellerPanel'))
      : this.site.text('becomeSeller', this.i18n.t('becomeSeller')),
  );
  private lastScrollY = 0;
  private scrollFrame = 0;
  private lastTouchY = 0;
  private readonly scrollListener = (): void => this.onWindowScroll();
  private readonly wheelListener = (event: WheelEvent): void => this.onWindowWheel(event);
  protected readonly dockItems = computed(() => {
    // Aymudo is the platform shell, not a marketplace storefront. Do not let
    // the generic fortune fallback leak its brand-specific routes here.
    if (this.site.config().brandKey === 'aymudo') {
      const configured = this.navItems()
        .filter((item) => ['products', 'solutions'].includes(item.routeKey ?? ''))
        .map((item) => ({
          key: item.routeKey ?? item.path,
          label: this.navLabel(item),
          icon: item.routeKey === 'solutions' ? '◇' : '▦',
          path: this.navLink(item),
          exact: item.exact ?? false,
        }));
      return [
        {
          key: 'home',
          label: this.i18n.t('home'),
          icon: '⌂',
          path: this.i18n.routePath('home'),
          exact: true,
        },
        ...configured,
      ];
    }
    if (this.isServiceSite()) {
      // Ceondent and Umut Demir are appointment-first service sites. Their
      // dock intentionally contains no categories or cart entry.
      return [
        {
          key: 'home',
          label: this.i18n.t('home'),
          icon: '⌂',
          path: this.i18n.routePath('home'),
          exact: true,
        },
        ...(this.site.enabled('appointments')
          ? [
              {
                key: 'appointment',
                label: this.site.text('appointmentPageTitle', 'Randevu al'),
                icon: '◉',
                path: this.i18n.routePath('appointment'),
                exact: false,
              },
            ]
          : []),
      ];
    }
    if (this.commerce()) {
      return [
        {
          key: 'home',
          label: this.i18n.t('home'),
          icon: '⌂',
          path: this.i18n.routePath('home'),
          exact: true,
        },
        {
          key: 'categories',
          label: this.i18n.t('categories'),
          icon: '▦',
          path: this.i18n.routePath('fortune'),
          exact: false,
        },
        ...(this.cartEnabled()
          ? [
              {
                key: 'cart',
                label: this.i18n.t('cart'),
                icon: '⌑',
                path: this.i18n.routePath('cart'),
                exact: false,
              },
            ]
          : []),
      ];
    }
    return [
      {
        key: 'home',
        label: this.i18n.t('home'),
        icon: '⌂',
        path: this.i18n.routePath('home'),
        exact: true,
      },
      {
        key: 'sellers',
        label: this.i18n.t('readers'),
        icon: '✦',
        path: this.i18n.routePath('sellers'),
        exact: false,
      },
      {
        key: 'fortune',
        label: this.site.experience('fortune')
          ? this.i18n.t('getFortune')
          : this.site.text('dockPrimary', this.site.text('navProducts', 'Ürünler')),
        icon: this.site.text('dockPrimaryIcon', '☕'),
        path: this.i18n.routePath('fortune'),
        exact: false,
      },
      {
        key: 'credit',
        label: this.i18n.t('buyCredits'),
        icon: '◆',
        path: this.i18n.routePath('creditTopUp'),
        exact: false,
      },
    ].filter((item) => this.pathEnabled(item.path));
  });

  constructor() {
    void this.loadCommerceCategories();
    effect(() => {
      if (this.showCreditBalance()) void this.auth.refreshCreditBalance();
    });
    effect(() => {
      if (this.showSellerPresence()) this.startSellerPresence();
      else this.stopSellerPresence();
    });
  }

  ngAfterViewInit(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    this.lastScrollY = this.scrollPosition();
    window.addEventListener('scroll', this.scrollListener, { capture: true, passive: true });
    document.addEventListener('scroll', this.scrollListener, { capture: true, passive: true });
    window.addEventListener('wheel', this.wheelListener, { capture: true, passive: true });
  }

  ngOnDestroy(): void {
    this.stopSellerPresence();
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    window.removeEventListener('scroll', this.scrollListener, true);
    document.removeEventListener('scroll', this.scrollListener, true);
    window.removeEventListener('wheel', this.wheelListener, true);
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
  }

  private startSellerPresence(): void {
    if (this.presenceStarted || typeof window === 'undefined') return;
    this.presenceStarted = true;
    const stored = window.localStorage.getItem(this.sellerPresenceStorageKey())
      || window.localStorage.getItem('aymudo.sellerPresence');
    if (stored === 'online' || stored === 'on_duty' || stored === 'offline') this.sellerPresence.set(stored);
    void this.sendSellerPresence(false);
    this.refreshPresenceTimer();
  }

  private stopSellerPresence(): void {
    this.presenceStarted = false;
    if (this.presenceTimer) clearInterval(this.presenceTimer);
    this.presenceTimer = null;
  }

  protected async setSellerPresence(status: 'online'|'on_duty'|'offline'): Promise<void> {
    if (this.sellerPresenceSaving()) return;
    const previous = this.sellerPresence();
    this.sellerPresence.set(status);
    this.sellerPresenceSaving.set(true);
    this.sellerPresenceFeedback.set(this.presenceCopy().saving);
    this.sellerPresenceError.set(false);
    const saved = await this.sendSellerPresence(true);
    this.sellerPresenceSaving.set(false);
    if (!saved) {
      this.sellerPresence.set(previous);
      this.sellerPresenceFeedback.set(this.presenceCopy().failed);
      this.sellerPresenceError.set(true);
      return;
    }
    if (typeof window !== 'undefined') window.localStorage.setItem(this.sellerPresenceStorageKey(), status);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('aymudo:seller-presence-changed', {
        detail: {
          sellerID: Number(this.auth.user()?.seller?.sellerID ?? this.auth.user()?.seller?.id ?? 0),
          status
        }
      }));
    }
    this.sellerPresenceFeedback.set(this.presenceCopy().saved);
    this.refreshPresenceTimer();
    if (typeof window !== 'undefined') window.setTimeout(() => {
      if (!this.sellerPresenceError()) this.sellerPresenceFeedback.set('');
    }, 1800);
  }

  private refreshPresenceTimer(): void {
    if (this.presenceTimer) clearInterval(this.presenceTimer);
    this.presenceTimer = null;
    if (typeof window === 'undefined' || this.sellerPresence() === 'offline') return;
    this.presenceTimer = setInterval(() => void this.sendSellerPresence(false), 30000);
  }

  private sellerPresenceStorageKey(): string {
    return `aymudo.sellerPresence.${Number(this.auth.user()?.id ?? 0)}`;
  }

  private async sendSellerPresence(explicit = false): Promise<boolean> {
    if (!this.showSellerPresence()) return false;
    try {
      const response: any = await firstValueFrom(this.api.post('livePresence', {
        status: this.sellerPresence(),
        explicit,
        heartbeat: !explicit
      }));
      const root = Array.isArray(response) ? response[0] : response;
      const result = root?.data?.result ?? root?.result ?? root?.apiData?.livePresence?.result;
      const payload = root?.data?.data ?? root?.data ?? root?.apiData?.livePresence?.data;
      if (result === false) return false;
      const confirmed = String(payload?.status ?? this.sellerPresence()).toLowerCase();
      if (confirmed === 'online' || confirmed === 'on_duty' || confirmed === 'offline') {
        this.sellerPresence.set(confirmed);
        if (typeof window !== 'undefined') window.localStorage.setItem(this.sellerPresenceStorageKey(), confirmed);
      }
      return true;
    } catch {
      return false;
    }
  }

  private async loadCommerceCategories(): Promise<void> {
    await this.site.ready();
    if (!this.commerce()) {
      return;
    }
    const categories = await this.fortune.categories();
    const roots = categories.filter((item) => !item.parentID).slice(0, 9);
    const rootIDs = new Set(roots.map((item) => item.id));
    this.headerCategories.set(
      categories.filter((item) => !item.parentID || rootIDs.has(Number(item.parentID))),
    );
  }

  protected categoryChildren(parentID: number): FortuneCategory[] {
    return this.headerCategories().filter((item) => Number(item.parentID) === parentID);
  }

  protected toggleMenu(): void {
    const next = !this.menuOpen();
    this.menuOpen.set(next);
    this.setMenuScrollLock(next);
    if (typeof document !== 'undefined') {
      window.setTimeout(() => {
        if (next) document.querySelector<HTMLElement>('.menu-sheet .sheet-close')?.focus();
        else this.focusVisibleMenuTrigger();
      }, 0);
    }
  }

  @HostListener('document:keydown.escape')
  protected closeMenuWithEscape(): void {
    if (!this.menuOpen()) return;
    this.closeMenu();
    if (typeof document !== 'undefined') {
      window.setTimeout(() => this.focusVisibleMenuTrigger(), 0);
    }
  }

  private focusVisibleMenuTrigger(): void {
    const triggers = Array.from(document.querySelectorAll<HTMLElement>('.menu-button, .mobile-dock button'));
    (triggers.find((element) => element.getClientRects().length > 0) ?? triggers[0])?.focus();
  }

  @HostListener('window:aymudo:auth-logout')
  protected onAuthLogout(): void {
    this.closeMenu();
    this.cartOpen.set(false);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
    this.setMenuScrollLock(false);
  }

  protected guidedActionLabel(): string {
    const configured = this.site.text('guidedActionLauncher', '');
    if (this.i18n.contentLang() === 'tr') return configured || 'İşlem yap';
    if (configured && !/^(kolay işlem|işlem yap)$/i.test(configured.trim())) return configured;
    return this.i18n.contentLang() === 'de' ? 'Schnellaktionen'
      : this.i18n.contentLang() === 'es' ? 'Acciones rápidas'
      : this.i18n.contentLang() === 'fr' ? 'Actions rapides'
      : 'Quick actions';
  }

  private setMenuScrollLock(locked: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }
    if (locked) {
      this.bodyOverflowBeforeMenu = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return;
    }
    document.body.style.overflow = this.bodyOverflowBeforeMenu;
  }

  protected navLink(item: { path: string; routeKey?: string }): string {
    if (item.routeKey) {
      const routePath = this.i18n.routePath(item.routeKey);
      // Runtime-only navigation groups such as managedServices do not exist
      // in every tenant route catalog. Preserve their explicit destination
      // instead of silently turning the dropdown heading into a home link.
      if (routePath !== '/' || item.path === '/') return routePath;
    }
    return this.i18n.localizedPath(item.path);
  }

  protected navLabel(item: { label: string; routeKey?: string; labelKey?: string }): string {
    if (item.routeKey === 'operationalWorkspace') {
      return (
        (
          {
            tr: 'Uygulama',
            en: 'App',
            de: 'App',
            es: 'Aplicación',
            fr: 'Application',
            ar: 'التطبيق',
            pt: 'Aplicativo',
            ru: 'Приложение',
            zh: '应用',
            hi: 'ऐप',
            ja: 'アプリ',
            ko: '앱',
            it: 'App',
            nl: 'App',
            id: 'Aplikasi',
          } as Record<string, string>
        )[this.i18n.lang()] ?? item.label
      );
    }
    if (item.labelKey) return this.site.text(item.labelKey, item.label);
    const fortuneBrand = this.site.experience('fortune');
    if (fortuneBrand && item.routeKey === 'freeFortune') {
      return (
        (
          {
            tr: 'Ücretsiz Fal',
            en: 'Free Reading',
            de: 'Kostenloses Orakel',
            es: 'Lectura gratis',
            fr: 'Voyance gratuite',
          } as Record<string, string>
        )[this.i18n.lang()] ?? 'Free Reading'
      );
    }
    if (!fortuneBrand)
      return item.routeKey ? this.site.text(`nav_${item.routeKey}`, item.label) : item.label;
    const keys: Record<
      string,
      'getFortune' | 'readers' | 'myReadings' | 'buyCredits' | 'support' | 'home' | 'categories'
    > = {
      fortune: 'getFortune',
      sellers: 'readers',
      orders: 'myReadings',
      creditTopUp: 'buyCredits',
      support: 'support',
      home: 'home',
      categories: 'categories',
    };
    if (item.routeKey && keys[item.routeKey]) return this.i18n.t(keys[item.routeKey]);
    return item.routeKey ? this.site.text(`nav_${item.routeKey}`, item.label) : item.label;
  }

  protected isAymudoSolutions(item: SiteNavItem): boolean {
    return this.site.config().brandKey === 'aymudo' && item.routeKey === 'solutions';
  }

  protected isAymudoMega(item: SiteNavItem): boolean {
    return (
      this.site.config().brandKey === 'aymudo' &&
      ['whyAymudo', 'products', 'solutions'].includes(item.routeKey ?? '')
    );
  }
  protected readonly subscribeCopy = computed(
    () => AYMUDO_SUBSCRIBE_COPY[this.i18n.contentLang()] ?? AYMUDO_SUBSCRIBE_COPY['en'],
  );
  protected readonly customerServiceCareerLabel = computed(() =>
    this.i18n.contentLang() === 'tr'
      ? 'Müşteri hizmetleri çalışanı ol'
      : 'Join customer service',
  );
  protected aymudoMegaIntro(routeKey?: string): { eyebrow: string; title: string; all: string } {
    const tr = this.i18n.contentLang() === 'tr';
    if (routeKey === 'whyAymudo')
      return tr
        ? {
            eyebrow: 'Neden Aymudo',
            title: 'İşletmenin bütün parçaları birlikte çalışsın.',
            all: 'Aymudo farkını keşfet',
          }
        : {
            eyebrow: 'Why Aymudo',
            title: 'Let every part of the business work together.',
            all: 'Discover the difference',
          };
    if (routeKey === 'solutions')
      return tr
        ? {
            eyebrow: 'İş modeline göre',
            title: 'Aynı kalıp değil, işletmene uyan sistem.',
            all: 'Tüm çözümleri gör',
          }
        : {
            eyebrow: 'By business model',
            title: 'Not one template. A system made for your work.',
            all: 'See all solutions',
          };
    return tr
      ? {
          eyebrow: 'Aymudo ürünleri',
          title: 'Kur, yönet, sat ve aynı sistemde büyüt.',
          all: 'Tüm ürünleri gör',
        }
      : {
          eyebrow: 'Aymudo products',
          title: 'Build, manage, sell and grow on one system.',
          all: 'See all products',
        };
  }
  protected aymudoMegaGroups(routeKey?: string): AymudoMegaGroup[] {
    const tr = this.i18n.contentLang() === 'tr';
    const products = this.i18n.routePath('products');
    if (routeKey === 'whyAymudo')
      return tr
        ? [
            {
              title: 'İşletmen için',
              links: [
                {
                  label: 'Bütün işin tek merkezde',
                  description: 'Ayrı araçlar yerine ortak veri ve operasyon.',
                  path: this.i18n.routePath('whyAymudo'),
                },
                {
                  label: 'İş modeline göre şekillenir',
                  description: 'Marka ve çalışma biçimi işletmene dönüşür.',
                  path: this.i18n.routePath('whyAymudo'),
                },
                {
                  label: 'Büyürken sistem değiştirme',
                  description: 'Şube, ekip, kanal ve ülke aynı omurgaya eklenir.',
                  path: this.i18n.routePath('whyAymudo'),
                },
              ],
            },
            {
              title: 'Aymudo’yu keşfet',
              links: [
                {
                  label: 'Gerçek işletmeler',
                  description: 'Aymudo üzerinde çalışan farklı dünyalar.',
                  path: this.i18n.routePath('whyAymudo'),
                },
                {
                  label: 'Tema motoru',
                  description: 'Altyapı ortak, marka deneyimi tamamen sana ait.',
                  path: products,
                  fragment: 'kur',
                },
                {
                  label: 'Global altyapı',
                  description: '15 dil, RTL ve yaklaşık 30 para birimi.',
                  path: products,
                  fragment: 'buyut',
                },
              ],
            },
          ]
        : [
            {
              title: 'For your business',
              links: [
                {
                  label: 'Everything in one centre',
                  description: 'Shared data and operations instead of separate tools.',
                  path: this.i18n.routePath('whyAymudo'),
                },
                {
                  label: 'Adapts to the business',
                  description: 'Brand and workflow become your own.',
                  path: this.i18n.routePath('whyAymudo'),
                },
                {
                  label: 'Grow without replacement',
                  description: 'Add branches, teams, channels and countries.',
                  path: this.i18n.routePath('whyAymudo'),
                },
              ],
            },
            {
              title: 'Discover Aymudo',
              links: [
                {
                  label: 'Real businesses',
                  description: 'Different worlds running on Aymudo.',
                  path: this.i18n.routePath('whyAymudo'),
                },
                {
                  label: 'Theme engine',
                  description: 'Shared core, completely individual brand.',
                  path: products,
                  fragment: 'build',
                },
                {
                  label: 'Global infrastructure',
                  description: '15 languages, RTL and nearly 30 currencies.',
                  path: products,
                  fragment: 'grow',
                },
              ],
            },
          ];
    if (routeKey === 'solutions') {
      const items = (this.site.config().solutions ?? [])
        .filter((item) => item.slug !== 'ozel-platform')
        .map((item) => ({
          label: this.solutionLabel(item),
          description: localizedSolution(item, this.i18n.contentLang()).audience,
          path: this.i18n.routePath('solution', { slug: item.slug }),
        }));
      return [
        {
          title: tr ? 'Satış ve ticaret' : 'Sales and commerce',
          links: items.filter((_, i) => [0, 3, 4, 7].includes(i)),
        },
        {
          title: tr ? 'Hizmet ve operasyon' : 'Services and operations',
          links: items.filter((_, i) => ![0, 3, 4, 7].includes(i)),
        },
      ];
    }
    return tr
      ? [
          {
            title: 'Kur ve yönet',
            links: [
              {
                label: 'Website ve tema motoru',
                description: 'Markana ait yayın ve tasarım sistemi.',
                path: this.productDetailPath('tema-motoru'),
              },
              {
                label: 'Randevu ve kaynaklar',
                description: 'Zamanı, hizmeti ve uygunluğu birlikte yönet.',
                path: this.productDetailPath('randevu'),
              },
              {
                label: 'Şube ve personel',
                description: 'Ekipleri, ekranları ve yetkileri bağla.',
                path: this.productDetailPath('sube-personel'),
              },
              {
                label: 'Mailbox ve Konuşmalar',
                description: 'E-posta, destek ve sosyal mesajları tek ekranda yönet.',
                path: this.productDetailPath('mailbox'),
              },
            ],
          },
          {
            title: 'Sat ve büyüt',
            links: [
              {
                label: 'E-ticaret ve stok',
                description: 'Üründen depoya, ödemeden kargoya.',
                path: this.productDetailPath('e-ticaret'),
              },
              {
                label: 'POS ve adisyon',
                description: 'Fiziksel satışı gerçek siparişe bağla.',
                path: this.productDetailPath('pos-kasa'),
              },
              {
                label: 'Pazaryeri',
                description: 'Satıcı, ürün ve komisyonu birlikte yönet.',
                path: this.productDetailPath('pazaryeri'),
              },
              {
                label: 'Pazarlama ve otomasyon',
                description: 'Kampanya, sosyal medya ve tetikleyiciler.',
                path: this.productDetailPath('tetikleyiciler'),
              },
            ],
          },
        ]
      : [
          {
            title: 'Build and manage',
            links: [
              {
                label: 'Website and themes',
                description: 'Publishing and design for your brand.',
                path: this.productDetailPath('tema-motoru'),
              },
              {
                label: 'Appointments and resources',
                description: 'Manage time, services and availability.',
                path: this.productDetailPath('randevu'),
              },
              {
                label: 'Branches and staff',
                description: 'Connect teams, screens and permissions.',
                path: this.productDetailPath('sube-personel'),
              },
              {
                label: 'Mailbox and conversations',
                description: 'Manage email, support and social messages in one screen.',
                path: this.productDetailPath('mailbox'),
              },
            ],
          },
          {
            title: 'Sell and grow',
            links: [
              {
                label: 'Commerce and inventory',
                description: 'Product, warehouse, payment and shipping.',
                path: this.productDetailPath('e-ticaret'),
              },
              {
                label: 'POS and tabs',
                description: 'Connect physical sales to real orders.',
                path: this.productDetailPath('pos-kasa'),
              },
              {
                label: 'Marketplace',
                description: 'Manage sellers, products and commissions.',
                path: this.productDetailPath('pazaryeri'),
              },
              {
                label: 'Marketing and automation',
                description: 'Campaigns, social and triggers.',
                path: this.productDetailPath('tetikleyiciler'),
              },
            ],
          },
        ];
  }

  protected solutionLabel(solution: SiteSolution): string {
    return localizedSolution(solution, this.i18n.contentLang()).title;
  }

  private productDetailPath(slug:string):string{
    return `${this.i18n.routePath('products').replace(/\/+$/, '')}/${slug}`;
  }

  protected solutionsMenuTitle(): string {
    const labels: Record<string, string> = {
      tr: 'İş modeline göre çözümler',
      en: 'Solutions by business model',
      de: 'Lösungen nach Geschäftsmodell',
      es: 'Soluciones por modelo de negocio',
      fr: 'Solutions par modèle économique',
      ar: 'حلول حسب نموذج العمل',
      pt: 'Soluções por modelo de negócio',
      ru: 'Решения по бизнес-модели',
      zh: '按业务模式选择解决方案',
      hi: 'व्यवसाय मॉडल के अनुसार समाधान',
      ja: 'ビジネスモデル別ソリューション',
      ko: '비즈니스 모델별 솔루션',
      it: 'Soluzioni per modello di business',
      nl: 'Oplossingen per bedrijfsmodel',
      id: 'Solusi berdasarkan model bisnis',
    };
    return labels[this.i18n.contentLang()] ?? labels['en'];
  }

  private shouldHideNavItem(item: { routeKey?: string }): boolean {
    return item.routeKey === 'becomeSeller';
  }

  private pathEnabled(path: string): boolean {
    const capability = capabilityForPath(path);
    return !capability || this.site.enabled(capability);
  }

  private currentUserIsSeller(): boolean {
    const user = this.auth.user();
    return (
      !!user?.seller?.isSeller ||
      Number(user?.seller?.sellerID ?? user?.seller?.id ?? 0) > 0 ||
      (user?.permissionProfiles ?? []).some((profile) => Number(profile.sellerID ?? 0) > 0) ||
      this.auth.can('seller')
    );
  }

  protected onWindowScroll(): void {
    if (this.scrollFrame || typeof window === 'undefined') return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.updateHeaderVisibility();
    });
  }

  private scrollPosition(): number {
    if (typeof document === 'undefined') return 0;
    const root = document.scrollingElement;
    return window.scrollY || root?.scrollTop || document.documentElement?.scrollTop || 0;
  }

  private updateHeaderVisibility(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const currentY = this.scrollPosition();
    if (this.menuOpen()) {
      this.headerHidden.set(false);
    } else if (currentY <= 24) {
      this.headerHidden.set(false);
    } else if (currentY > this.lastScrollY + 2 && currentY > 120) {
      this.headerHidden.set(true);
    } else if (currentY < this.lastScrollY - 2) {
      this.headerHidden.set(false);
    }
    this.lastScrollY = currentY;
  }

  protected onWindowWheel(event: WheelEvent): void {
    if (this.menuOpen()) {
      this.headerHidden.set(false);
      return;
    }
    const currentY = this.scrollPosition();
    if (event.deltaY < 0) {
      this.headerHidden.set(false);
    } else if (event.deltaY > 0 && currentY > 120) {
      this.headerHidden.set(true);
    }
  }

  @HostListener('window:touchstart', ['$event'])
  protected onTouchStart(event: TouchEvent): void {
    this.lastTouchY = event.touches[0]?.clientY ?? 0;
  }

  @HostListener('window:touchmove', ['$event'])
  protected onTouchMove(event: TouchEvent): void {
    if (this.menuOpen()) {
      this.headerHidden.set(false);
      return;
    }
    const touchY = event.touches[0]?.clientY ?? this.lastTouchY;
    const currentY = this.scrollPosition();
    if (touchY > this.lastTouchY + 2) {
      this.headerHidden.set(false);
    } else if (touchY < this.lastTouchY - 2 && currentY > 120) {
      this.headerHidden.set(true);
    }
    this.lastTouchY = touchY;
  }
}

const AYMUDO_SUBSCRIBE_COPY: Record<string, { eyebrow: string; title: string; button: string }> = {
  tr: {
    eyebrow: 'Başlamaya hazır',
    title: 'İşletmenin arkasındaki sistemi kur.',
    button: 'Abone ol',
  },
  en: {
    eyebrow: 'Ready to begin',
    title: 'Build the system behind your business.',
    button: 'Subscribe',
  },
  de: {
    eyebrow: 'Bereit zu starten',
    title: 'Bauen Sie das System hinter Ihrem Unternehmen.',
    button: 'Abonnieren',
  },
  es: {
    eyebrow: 'Todo listo para empezar',
    title: 'Construye el sistema detrás de tu negocio.',
    button: 'Suscribirse',
  },
  fr: {
    eyebrow: 'Prêt à commencer',
    title: 'Construisez le système derrière votre entreprise.',
    button: 'S’abonner',
  },
  ar: { eyebrow: 'جاهز للبدء', title: 'أنشئ النظام الذي يعمل خلف أعمالك.', button: 'اشترك' },
  pt: {
    eyebrow: 'Pronto para começar',
    title: 'Construa o sistema por trás da sua empresa.',
    button: 'Assinar',
  },
  ru: {
    eyebrow: 'Готовы начать',
    title: 'Создайте систему, которая стоит за вашим бизнесом.',
    button: 'Подписаться',
  },
  zh: { eyebrow: '准备开始', title: '构建企业背后的系统。', button: '订阅' },
  hi: {
    eyebrow: 'शुरू करने के लिए तैयार',
    title: 'अपने व्यवसाय के पीछे का सिस्टम बनाएं।',
    button: 'सदस्य बनें',
  },
  ja: {
    eyebrow: '始める準備はできましたか',
    title: 'ビジネスを支えるシステムを構築。',
    button: '申し込む',
  },
  ko: {
    eyebrow: '시작할 준비가 되셨나요',
    title: '비즈니스를 뒷받침하는 시스템을 구축하세요.',
    button: '구독하기',
  },
  it: {
    eyebrow: 'Pronto a iniziare',
    title: 'Costruisci il sistema dietro la tua impresa.',
    button: 'Abbonati',
  },
  nl: {
    eyebrow: 'Klaar om te beginnen',
    title: 'Bouw het systeem achter uw bedrijf.',
    button: 'Abonneren',
  },
  id: {
    eyebrow: 'Siap memulai',
    title: 'Bangun sistem di balik bisnis Anda.',
    button: 'Berlangganan',
  },
};
